package com.arena_web.ui.sections.chat.mobile

import androidx.compose.runtime.*
import com.arena_web.data.dto.ChatDto
import com.arena_web.data.dto.ChatListItemDto
import com.arena_web.data.dto.MessageDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.EMPTY_STATE_GLYPH_COLOR
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.w3c.dom.events.Event

// Figma 8538:395381 — mobile chat-room view.
@Composable
fun MobileChatRoom(
    // Either source is enough to draw the header instantly — the room
    // screen opens on tap and `chat` arrives later once the detail fetch
    // resolves. We derive name/avatar/online from whichever is available.
    chat: ChatDto?,
    fallbackListItem: ChatListItemDto?,
    isLoading: Boolean,
    messages: List<MessageDto>,
    messageText: String,
    onlineUserIds: Set<String>,
    currentUserId: String?,
    onBack: () -> Unit,
    onAvatarClick: () -> Unit = {},
    onTextChanged: (String) -> Unit,
    onSend: () -> Unit,
    onTyping: () -> Unit,
) {
    val t = LocalAppStrings.current
    val type = (chat?.type ?: fallbackListItem?.type ?: "DIRECT").uppercase()
    // For direct chats the "partner" is the other member; for group chats we
    // skip the online indicator.
    val partnerMember = if (type == "DIRECT") {
        chat?.members?.firstOrNull { it.userId != currentUserId }
    } else null
    val partnerUserId = when {
        type != "DIRECT" -> null
        partnerMember != null -> partnerMember.userId
        else -> fallbackListItem?.otherUserId
    }

    val name = chat?.name
        ?: fallbackListItem?.name
        ?: partnerMember?.fullName?.takeIf { it.isNotBlank() }
        ?: partnerMember?.username
        ?: t.chatDefaultName
    val imageUrl = chat?.imageUrl ?: fallbackListItem?.imageUrl ?: partnerMember?.avatarUrl
    // Prefer the cached list-item card (already on disk), fall back to the
    // ChatMember row that the chat-detail fetch brings down — without this
    // fallback a deep-link refresh shows a round placeholder until the user
    // navigates away and back, because the list itself isn't loaded.
    val cardImageUrl = fallbackListItem?.cardImageUrl ?: partnerMember?.cardImageUrl
    val cardAvatarUrl = fallbackListItem?.cardAvatarUrl ?: partnerMember?.cardAvatarUrl
    val partnerOnline = partnerUserId?.let { it in onlineUserIds } == true
    val membersCount = chat?.members?.size
    val status = when {
        type != "DIRECT" -> membersCount?.let { t.chatMembersCount(it) } ?: ""
        partnerOnline -> t.chatOnline
        else -> t.chatOffline
    }
    // While both the cached list-item and the loaded chat detail are still
    // null, we have nothing real to draw — render only the back button so
    // the user doesn't see placeholder "Chat" / "Offline" / round-avatar
    // flashing while the network resolves.
    val hasData = chat != null || fallbackListItem != null

    // Keyboard handling — top bar must NOT move. Strategy:
    //   1. Hard-lock <html> + <body> with `position: fixed` so iOS Safari
    //      can't visually pan the page when an input is focused. (Modern
    //      browsers honour `interactive-widget=resizes-content` viewport
    //      meta and shrink the layout viewport itself — see build.gradle.kts.
    //      The position-fixed lock here is the belt for older iOS where
    //      that doesn't apply.)
    //   2. As a fallback for browsers without interactive-widget support,
    //      track the keyboard height via visualViewport into `--mcr-kbd`
    //      and add it to the chat column's padding-bottom — the input row
    //      gets pushed up by the keyboard height while the top bar stays
    //      exactly in place.
    DisposableEffect(Unit) {
        val rootStyle = document.documentElement?.asDynamic()?.style
        val bodyStyle = document.body?.asDynamic()?.style

        val prevHtmlPosition = rootStyle?.position as? String ?: ""
        val prevHtmlOverflow = rootStyle?.overflow as? String ?: ""
        val prevHtmlWidth = rootStyle?.width as? String ?: ""
        val prevHtmlHeight = rootStyle?.height as? String ?: ""
        val prevHtmlTop = rootStyle?.top as? String ?: ""
        val prevHtmlLeft = rootStyle?.left as? String ?: ""
        val prevBodyPosition = bodyStyle?.position as? String ?: ""
        val prevBodyOverflow = bodyStyle?.overflow as? String ?: ""
        val prevBodyWidth = bodyStyle?.width as? String ?: ""
        val prevBodyHeight = bodyStyle?.height as? String ?: ""
        val prevBodyTop = bodyStyle?.top as? String ?: ""
        val prevBodyLeft = bodyStyle?.left as? String ?: ""

        // Apply via setProperty with "important" priority so the lock beats
        // any global CSS rule (Kobweb/Silk style sheets, app body styles).
        rootStyle?.setProperty("position", "fixed", "important")
        rootStyle?.setProperty("overflow", "hidden", "important")
        rootStyle?.setProperty("top", "0", "important")
        rootStyle?.setProperty("left", "0", "important")
        rootStyle?.setProperty("width", "100%", "important")
        rootStyle?.setProperty("height", "100%", "important")
        bodyStyle?.setProperty("position", "fixed", "important")
        bodyStyle?.setProperty("overflow", "hidden", "important")
        bodyStyle?.setProperty("top", "0", "important")
        bodyStyle?.setProperty("left", "0", "important")
        bodyStyle?.setProperty("width", "100%", "important")
        bodyStyle?.setProperty("height", "100%", "important")

        val vv = window.asDynamic().visualViewport
        val update: () -> Unit = {
            val winH = window.innerHeight.toDouble()
            val vvH: Double = if (vv != null) {
                (vv.height as? Double) ?: winH
            } else winH
            // Only treat it as an open keyboard when the shrink is large
            // enough; smaller deltas come from URL-bar / chrome animations
            // and shouldn't move the input row. With
            // interactive-widget=resizes-content this will be ~0 (layout
            // viewport already shrinks natively).
            val kbd = (winH - vvH).coerceAtLeast(0.0)
            if (kbd > 100) {
                rootStyle?.setProperty("--mcr-kbd", "${kbd}px")
            } else {
                rootStyle?.removeProperty("--mcr-kbd")
            }
        }
        update()

        val onChange: (Event) -> Unit = { update() }
        if (vv != null) {
            vv.addEventListener("resize", onChange)
            vv.addEventListener("scroll", onChange)
        }
        window.addEventListener("resize", onChange)

        onDispose {
            if (vv != null) {
                vv.removeEventListener("resize", onChange)
                vv.removeEventListener("scroll", onChange)
            }
            window.removeEventListener("resize", onChange)
            rootStyle?.removeProperty("--mcr-kbd")
            rootStyle?.position = prevHtmlPosition
            rootStyle?.overflow = prevHtmlOverflow
            rootStyle?.top = prevHtmlTop
            rootStyle?.left = prevHtmlLeft
            rootStyle?.width = prevHtmlWidth
            rootStyle?.height = prevHtmlHeight
            bodyStyle?.position = prevBodyPosition
            bodyStyle?.overflow = prevBodyOverflow
            bodyStyle?.top = prevBodyTop
            bodyStyle?.left = prevBodyLeft
            bodyStyle?.width = prevBodyWidth
            bodyStyle?.height = prevBodyHeight
        }
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                // Community/group rooms sit a touch lower so the round group
                // avatar + "N members" header doesn't crowd the top edge.
                property("padding-top", if (type == "DIRECT") "8px" else "14px")
                // Bottom padding = safe-area + keyboard. When --mcr-kbd is
                // set (keyboard open), the input row gets pushed up by the
                // keyboard height while the top bar stays exactly in place.
                property(
                    "padding-bottom",
                    "calc(max(16px, env(safe-area-inset-bottom, 0px)) + var(--mcr-kbd, 0px))"
                )
                property("overflow", "hidden")
            },
    ) {
        // Top row — back + name/status + avatar. Pinned (flex-shrink: 0) so
        // only the message list scrolls.
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 12.px)
                .flexShrink(0),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            // Back button — 44×44 with our gradient border.
            Box(
                modifier = Modifier
                    .size(44.px)
                    .borderRadius(50.percent)
                    .cursor(Cursor.Pointer)
                    .onClick { onBack() }
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "${AppGradients.InteractiveSecondary} padding-box, " +
                                "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                    },
                contentAlignment = Alignment.Center,
            ) {
                // Chevron-back: reuse forward arrow rotated 180°.
                Icon(
                    src = AppIcons.ChevronForward,
                    modifier = Modifier.size(18.px).styleModifier {
                        property("filter", AppColors.IconDefaultFilter)
                        property("transform", "rotate(180deg)")
                    }
                )
            }

            // Centre name + status — hidden until at least one source of
            // data (list item snapshot or loaded ChatDto) is available, so
            // the user never sees the "Chat" / "Offline" placeholder flash.
            if (hasData) {
                Column(
                    modifier = Modifier.gap(6.px).styleModifier { property("min-width", "0") },
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    SpanText(
                        name,
                        modifier = Modifier
                            .fontSize(20.px)
                            .fontWeight(500)
                            .lineHeight(26.px)
                            .color(AppColors.Text.Secondary)
                            .styleModifier { property("white-space", "nowrap") }
                    )
                    SpanText(
                        status,
                        modifier = Modifier
                            .fontSize(14.px)
                            .fontWeight(600)
                            .lineHeight(14.px)
                            .color(if (partnerOnline) Color("#00E5A4") else Color("#99B1BF"))
                    )
                }
            }

            // Trailing avatar — only when we have real data; otherwise we
            // leave the slot empty rather than show a placeholder circle.
            if (hasData && cardImageUrl != null) {
                // Override `--scale` locally so the PlayerCard renders bigger
                // than the default mobile scale. 0.8 px ≈ XtraSmall * 2x so
                // the trailing avatar reads as a proper player-card thumb
                // instead of a tiny postage stamp.
                Box(
                    modifier = Modifier
                        .flexShrink(0)
                        .cursor(Cursor.Pointer)
                        .onClick { onAvatarClick() }
                        .styleModifier { property("--scale", "0.65px") }
                ) {
                    PlayerCard(
                        playerName = "",
                        rating = 0,
                        position = "",
                        sport = "football",
                        size = PlayerCardSize.XtraSmall,
                        cardAssetsBasePath = null,
                        cardTextColor = null,
                        cardFrameUrl = cardImageUrl,
                        avatarUrl = cardAvatarUrl ?: AppIcons.EmptyAvatar,
                        hideRating = true,
                        hideShadow = true,
                    )
                }
            } else if (hasData) {
                Box(
                    modifier = Modifier
                        .cursor(Cursor.Pointer)
                        .onClick { onAvatarClick() }
                ) {
                    RoundAvatar(name = name, imageUrl = imageUrl)
                }
            }
        }

        // Messages — auto-scroll to bottom when new content arrives.
        val listId = remember { "mobile-chat-room-${kotlin.random.Random.nextInt(1_000_000)}" }
        val chatKey = chat?.id ?: fallbackListItem?.id
        LaunchedEffect(messages.size, chatKey) {
            kotlinx.browser.document.getElementById(listId)?.let { el ->
                el.scrollTop = el.scrollHeight.toDouble()
            }
        }
        Div(
            attrs = Modifier
                .fillMaxWidth()
                .flexGrow(1)
                .id(listId)
                .styleModifier {
                    property("min-height", "0")
                    property("overflow-y", "auto")
                    property("overflow-x", "hidden")
                    // Prevent rubber-band/overscroll from bubbling up to the
                    // page, so dragging at the edges of the message list
                    // doesn't move the surrounding chrome.
                    property("overscroll-behavior", "contain")
                    property("scrollbar-width", "none")
                    property("display", "flex")
                    property("flex-direction", "column")
                    // Anchor messages to the bottom — when there are few
                    // messages they sit just above the input; when there are
                    // many they fill from the top with newest at the bottom
                    // and the user scrolls up to see history.
                    property("justify-content", "flex-end")
                    property("gap", "12px")
                    property("padding", "8px 4px 12px 4px")
                }
                .toAttrs()
        ) {
            if (messages.isEmpty()) {
                // Empty conversation — centered placeholder (the list is
                // justify-content:flex-end, so a flex-grow child centers it).
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1).styleModifier { property("min-height", "0") },
                    contentAlignment = Alignment.Center,
                ) {
                    ChatRoomEmptyState()
                }
            } else {
                messages.forEach { msg ->
                    val isMine = currentUserId != null && msg.senderId == currentUserId
                    MessageBubble(message = msg, isMine = isMine)
                }
            }
        }

        // Input row. Pinned (flex-shrink: 0) so it never collapses when the
        // message list overflows.
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(48.px)
                .flexShrink(0)
                .borderRadius(28.px)
                .padding(left = 16.px, right = 12.px)
                .gap(4.px)
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(20.px)
                    .flexShrink(0)
                    .styleModifier {
                        property("background-color", "#99B1BF")
                        property("opacity", "0.5")
                        property("mask", "url('${AppIcons.Attachment}') no-repeat center / contain")
                        property("-webkit-mask", "url('${AppIcons.Attachment}') no-repeat center / contain")
                    }
            )
            Input(
                type = InputType.Text,
                attrs = {
                    value(messageText)
                    placeholder(t.chatMessageInputPlaceholder)
                    onInput {
                        onTextChanged(it.value)
                        onTyping()
                    }
                    onKeyDown {
                        if (it.key == "Enter" && !it.shiftKey && messageText.isNotBlank()) {
                            it.preventDefault()
                            onSend()
                        }
                    }
                    style {
                        property("flex", "1")
                        property("background", "transparent")
                        property("border", "none")
                        property("outline", "none")
                        property("color", "#BED6F5")
                        property("font-family", "'Urbanist', sans-serif")
                        property("font-size", "16px")
                        property("min-width", "0")
                    }
                }
            )
            Box(
                modifier = Modifier
                    .size(32.px)
                    .borderRadius(50.percent)
                    .backgroundColor(Color("#0061FF"))
                    .cursor(if (messageText.isNotBlank()) Cursor.Pointer else Cursor.NotAllowed)
                    .onClick { if (messageText.isNotBlank()) onSend() }
                    .styleModifier {
                        if (messageText.isBlank()) property("opacity", "0.5")
                    },
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    src = AppIcons.Send,
                    modifier = Modifier.size(16.px).styleModifier {
                        property("filter", "brightness(0) invert(1)")
                    }
                )
            }
        }
    }
}

// Empty conversation placeholder — chatbubbles icon + prompt to say hi. Raw px
// to match the page (full mobile screen, not a `--scale`-aliased sheet).
@Composable
private fun ChatRoomEmptyState() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().padding(leftRight = 24.px).gap(16.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Canonical muted-blue empty-state tint (matches desktop TintedGlyph).
        Box(
            modifier = Modifier.size(56.px).styleModifier {
                property("background-color", EMPTY_STATE_GLYPH_COLOR)
                property("-webkit-mask", "url('${AppIcons.EmptyChats}') no-repeat center / contain")
                property("mask", "url('${AppIcons.EmptyChats}') no-repeat center / contain")
            }
        )
        Column(
            modifier = Modifier.fillMaxWidth().gap(8.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.chatRoomEmptyTitle,
                modifier = Modifier
                    .fontSize(24.px)
                    .fontWeight(AppTypography.Medium)
                    .lineHeight(32.px)
                    .color(AppColors.Text.Secondary)
                    .textAlign(TextAlign.Center),
            )
            SpanText(
                t.chatRoomEmptySubtitle,
                modifier = Modifier
                    .fillMaxWidth()
                    .fontSize(14.px)
                    .fontWeight(AppTypography.Regular)
                    .lineHeight(22.px)
                    .color(Color("#99B1BF"))
                    .textAlign(TextAlign.Center),
            )
        }
    }
}

@Composable
private fun MessageBubble(message: MessageDto, isMine: Boolean) {
    val time = formatTime(message.createdAt)
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = if (isMine) Arrangement.End else Arrangement.Start,
    ) {
        Box(
            modifier = Modifier
                .styleModifier {
                    property("max-width", "78%")
                    if (isMine) {
                        property("background", "#0A2E67")
                    } else {
                        property("background", "rgba(9,20,45,0.8)")
                    }
                }
                .borderRadius(14.px)
                .padding(leftRight = 14.px, topBottom = 8.px)
        ) {
            Column(modifier = Modifier.gap(2.px)) {
                SpanText(
                    message.content,
                    modifier = Modifier
                        .fontSize(15.px)
                        .color(if (isMine) Color.white else AppColors.Text.Secondary)
                        .styleModifier {
                            property("line-height", "1.5")
                            property("white-space", "pre-wrap")
                            property("word-break", "break-word")
                        }
                )
                Row(
                    modifier = Modifier.fillMaxWidth().gap(4.px),
                    horizontalArrangement = Arrangement.End,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(
                        time,
                        modifier = Modifier
                            .fontSize(11.px)
                            .color(Color("#99B1BF"))
                    )
                    if (isMine) {
                        Icon(
                            src = AppIcons.Check,
                            modifier = Modifier.size(12.px).styleModifier {
                                property(
                                    "filter",
                                    if (message.isRead) {
                                        // Blue-ish tint for "seen".
                                        "brightness(0) saturate(100%) invert(35%) sepia(98%) " +
                                            "saturate(2000%) hue-rotate(210deg) brightness(100%) contrast(100%)"
                                    } else {
                                        AppColors.IconDefaultFilter
                                    }
                                )
                            }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun RoundAvatar(name: String, imageUrl: String?) {
    val initials = name.split(" ")
        .filter { it.isNotBlank() }
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "?" }
    Box(
        modifier = Modifier
            .size(40.px)
            .flexShrink(0)
            .borderRadius(50.percent)
            .overflow(Overflow.Hidden)
            .backgroundColor(Color("#1A2238")),
        contentAlignment = Alignment.Center,
    ) {
        if (imageUrl != null) {
            Div(attrs = {
                ref { el ->
                    el.innerHTML = "<img src=\"$imageUrl\" referrerpolicy=\"no-referrer\" " +
                        "style=\"width:100%;height:100%;object-fit:cover;\" />"
                    onDispose {}
                }
                style { property("width", "100%"); property("height", "100%") }
            })
        } else {
            SpanText(
                initials,
                modifier = Modifier.fontSize(16.px).color(Color("#FAFAFB"))
            )
        }
    }
}

private fun formatTime(iso: String?): String {
    if (iso.isNullOrBlank()) return ""
    return try {
        val d = kotlin.js.Date(iso)
        val h24 = d.getHours()
        val mins = d.getMinutes()
        val period = if (h24 < 12) "AM" else "PM"
        val hh = ((h24 % 12).let { if (it == 0) 12 else it })
        val mm = mins.toString().padStart(2, '0')
        "$hh:$mm $period"
    } catch (_: Throwable) {
        ""
    }
}














